<div class="save-container">
    <ng-container *ngIf="isEditing; else savedButtons">
        <div class="button cancel" (click)="cancel()">
            {{ 'output-settings.DO.settings.cancel' | translate }}
        </div>
        <div class="button save" [class.saveDisabled]="!isSaveEnabled()" (click)="save()">
            {{ 'output-settings.DO.settings.save' | translate }}
        </div>
    </ng-container>

    <ng-template #savedButtons>
        <div class="row-button" (click)="deleteSetting()">
            <mat-icon class="small-icon" svgIcon="delete" />
            {{ 'output-settings.DO.settings.delete' | translate }}
        </div>
        <div class="row-button" (click)="isEditing = true">
            <mat-icon class="small-icon" svgIcon="edit-pen" />
            {{ 'output-settings.DO.settings.edit' | translate }}
        </div>
    </ng-template>
</div>

<div class="signal-row">
    <div class="setting-option-container">
        <div class="dropdown-title">
            {{ 'output-settings.DO.settings.signal-type' | translate }}
        </div>
        <ng-container *ngIf="isEditing; else signalType">
            <div
                class="selector"
                (click)="openSignalSelector($event)"
                [class.focusedInput]="isSignalTypeOpen"
                #signalSelElement>
                <ng-container
                    *ngTemplateOutlet="
                        selectorTemplate;
                        context: {
                            selector: signalSelector,
                            displayValue: setting.signalType,
                            isOpen: isSignalTypeOpen,
                            options: getSelectorOptions(signalSelector),
                            onSelect: optionSelected.bind(this, signalSelector),
                        }
                    " />
            </div>
        </ng-container>
        <ng-template #signalType>
            <div class="locked-text">
                {{ getOptionString(signalSelector, setting.signalType) | translate }}
            </div>
        </ng-template>
    </div>

    <div class="setting-option-container" *ngIf="isOneShot">
        <div class="dropdown-title">
            {{ 'output-settings.DO.settings.signal-width' | translate }}
        </div>
        <ng-container *ngIf="isEditing; else signalWidth">
            <app-simple-input
                class="signal-width-input"
                [inputValue]="setting.signalWidth"
                propertyName="signalWidth"
                rightText="output-settings.DO.settings.signal-width-unit"
                [isNumberInput]="true"
                [errorIsAbsolute]="true"
                (onInputUpdated)="handleSignalWidthChange($event)">
            </app-simple-input>
            <div class="signal-width-range" [class.invalid]="!isSignalWidthValid()">
                {{ 'output-settings.DO.settings.signal-width-range' | translate }}
            </div>
        </ng-container>
        <ng-template #signalWidth>
            <div class="locked-text">
                {{ setting.signalWidth }} {{ 'output-settings.DO.settings.signal-width-unit' | translate }}
            </div>
        </ng-template>
    </div>
</div>

<div class="setting-option-container">
    <div class="dropdown-title">
        {{ 'output-settings.DO.settings.output-mode' | translate }}
    </div>
    <ng-container *ngIf="isEditing; else ouputMode">
        <div
            class="selector"
            (click)="openModeSelector($event)"
            [class.focusedInput]="isOutputModeOpen"
            #modeSelElement>
            <ng-container
                *ngTemplateOutlet="
                    selectorTemplate;
                    context: {
                        selector: modeSelector,
                        displayValue: setting.outputMode,
                        isOpen: isOutputModeOpen,
                        options: getSelectorOptions(modeSelector),
                        onSelect: optionSelected.bind(this, modeSelector),
                    }
                " />
        </div>
    </ng-container>
    <ng-template #ouputMode>
        <div class="locked-text">
            {{ getOptionString(modeSelector, setting.outputMode) | translate }}
        </div>
    </ng-template>
</div>

<div class="setting-option-container">
    <div class="dropdown-title">
        {{ 'output-settings.DO.settings.source-channels' | translate }}
    </div>

    <div class="source-channels-table" [ngStyle]="{ maxHeight: isTableExpanded ? '518px' : '232px' }">
        <div class="table-row table-header">
            <div class="num-column header-text">
                {{ 'output-settings.DO.source-table.header.num' | translate }}
            </div>
            <div class="name-column header-text">
                {{ 'output-settings.DO.source-table.header.name' | translate }}
            </div>
            <div class="conditions-column header-text">
                {{ 'output-settings.DO.source-table.header.condition' | translate }}
            </div>
        </div>
        <div class="channel-rows" #settingChannels>
            <ng-container *ngIf="hasNoChannels()">
                <div class="empty-table-text">
                    {{ 'output-settings.DO.source-table.no-channels' | translate }}
                </div>
            </ng-container>
            <div *ngFor="let settingChannel of setting.channels" class="table-row">
                <div class="num-column">
                    {{ settingChannel.index }}
                </div>
                <div class="name-column selected-channel-wrapper">
                    <ng-container *ngIf="isEditing; else channelName">
                        <div class="channel-name-wrapper text-overflow" (click)="updateChannel(settingChannel.index)">
                            {{ getChannelName(settingChannel) }}
                        </div>
                        <div class="channel-remove-wrapper" (click)="removeChannel(settingChannel.index - 1)">
                            <mat-icon svgIcon="delete" class="small-icon" />
                            <div>
                                {{ 'output-settings.AO.table.channel.clear' | translate }}
                            </div>
                        </div>
                    </ng-container>
                    <ng-template #channelName>
                        <div class="simple-channel-name text-overflow">
                            {{ getChannelName(settingChannel) }}
                        </div>
                    </ng-template>
                </div>
                <div class="conditions-column">
                    <div>
                        {{ getTriggerCondition(settingChannel) | translate }}
                    </div>
                    <div class="text-overflow">
                        {{ getCondition(settingChannel) }}
                    </div>
                </div>
            </div>
        </div>

        <div class="table-footer">
            <div class="table-footer-row">
                <ng-container *ngIf="isEditing">
                    <div class="footer-button" (click)="addChannel()" [class.disabled]="isAddChannelDisabled()">
                        {{ 'output-settings.DO.source-table.footer.add' | translate }}
                    </div>
                </ng-container>

                <div class="footer-slots">
                    {{
                        'output-settings.DO.source-table.footer.channels'
                            | translate: { num: setting.channels?.length || 0 }
                    }}
                </div>
            </div>
            <div
                (click)="isTableExpanded = !isTableExpanded"
                class="footer-button"
                [class.disabled]="isExpandDisabled()">
                {{
                    isTableExpanded
                        ? ('output-settings.DO.source-table.footer.collapse' | translate)
                        : ('output-settings.DO.source-table.footer.expand' | translate)
                }}
            </div>
        </div>
    </div>
</div>

<ng-container *ngIf="isEditing || hasSourceCondition()">
    <div class="source-channel-condition" [class.disable-conditions]="!shouldEnableSourceCondition()">
        <div class="source-condition-title">
            <div>
                {{ 'output-settings.DO.dialog.output.source-condition.title' | translate }}
            </div>

            <mat-icon
                svgIcon="question-mark"
                style="cursor: pointer"
                class="small-icon"
                (click)="isInfoMessageVisible = !isInfoMessageVisible; $event.stopPropagation()" />
            <div class="info-message-container" *ngIf="isInfoMessageVisible">
                <div class="info-row">
                    <span class="semibold">
                        {{ 'output-settings.DO.dialog.output.source-condition.or' | translate }}
                    </span>
                    {{ 'output-settings.DO.dialog.output.source-condition.info-or' | translate }}
                </div>
                <div class="info-row">
                    <span class="semibold black">
                        {{ 'output-settings.DO.dialog.output.source-condition.and' | translate }}
                    </span>
                    {{ 'output-settings.DO.dialog.output.source-condition.info-and' | translate }}
                </div>
            </div>
        </div>
        <ng-container *ngIf="isEditing; else sourceConditionText">
            <mat-radio-group class="option-row">
                <mat-radio-button
                    [checked]="isOrCondition()"
                    [value]="isOrCondition()"
                    (click)="updateType($event, OR)">
                    {{ 'output-settings.DO.dialog.output.source-condition.or' | translate }}
                </mat-radio-button>

                <mat-radio-button
                    [checked]="isAndCondition()"
                    [value]="isAndCondition()"
                    (click)="updateType($event, AND)">
                    {{ 'output-settings.DO.dialog.output.source-condition.and' | translate }}
                </mat-radio-button>
            </mat-radio-group>
        </ng-container>
        <ng-template #sourceConditionText>
            <div class="source-condition-text">
                {{
                    setting.sourceChannelCondition == OR
                        ? ('output-settings.DO.dialog.output.source-condition.or' | translate)
                        : setting.sourceChannelCondition == AND
                          ? ('output-settings.DO.dialog.output.source-condition.and' | translate)
                          : ''
                }}
            </div>
        </ng-template>
    </div>
</ng-container>

<!-- Selector template -->
<ng-template
    #selectorTemplate
    let-selector="selector"
    let-displayValue="displayValue"
    let-isOpen="isOpen"
    let-options="options"
    let-onSelect="onSelect">
    <div [class.selected]="displayValue">
        {{
            displayValue
                ? (getOptionString(selector, displayValue) | translate)
                : ('output-settings.DO.selector.placeholder' | translate)
        }}
    </div>
    <mat-icon class="arrow" svgIcon="dropdown-arrow" />
    <ng-container *ngIf="isOpen">
        <div class="options-wrapper" #selectorElement [style.top]="optionsTop" [style.left]="optionsLeft">
            <div
                *ngFor="let option of options"
                class="selector-option"
                (click)="onSelect(option)"
                [class.selectedOption]="displayValue == option">
                <div>
                    {{ getOptionString(selector, option) | translate }}
                </div>
                <ng-container *ngIf="displayValue == option">
                    <mat-icon class="small-icon" svgIcon="tick"
                /></ng-container>
            </div>
        </div>
    </ng-container>
</ng-template>
